import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

// CommonMark folds a line directly after a list item into that item; the agent
// can't be relied on to leave the closing blank line, so insert one.
// Ordered lists close only when the next numbered line doesn't continue the
// sequence: only a list starting at 1 can interrupt a paragraph, so closing
// early would turn "2. ..." into prose.
const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s+/;
const ORDERED_ITEM = /^\s*(\d+)[.)]\s+/;

const orderedListResumes = (
  lines: readonly string[],
  from: number,
  previous: number,
): boolean => {
  for (let j = from; j < lines.length && lines[j].trim() !== ""; j += 1) {
    const match = ORDERED_ITEM.exec(lines[j]);
    if (match) return Number(match[1]) === previous + 1;
  }
  return false;
};

const closeListsBeforeProse = (text: string): string => {
  const lines = text.split("\n");
  const out: string[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    out.push(line);
    const next = lines[i + 1];
    if (
      next === undefined ||
      !LIST_ITEM.test(line) ||
      next.trim() === "" ||
      LIST_ITEM.test(next) ||
      /^\s/.test(next)
    ) {
      continue;
    }
    const ordered = ORDERED_ITEM.exec(line);
    if (ordered && orderedListResumes(lines, i + 2, Number(ordered[1]))) {
      continue;
    }
    out.push("");
  }
  return out.join("\n");
};

/** Images render as alt text, never fetched, so a tracking URL can't leak a patient name off-clinic. */
export const Markdown = ({ children }: { children: string }) => (
  <div className="ds-markdown min-w-0">
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        img: ({ alt }) => (alt ? <span>{alt}</span> : null),
      }}
    >
      {closeListsBeforeProse(children)}
    </ReactMarkdown>
  </div>
);
